import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoggerService, RepairService, InventoryService } from '../../services';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { WorkshopService } from '../../services/workshop.service';

export interface DialogData {
  type: string;
  variant: any;
}

@Component({
  selector: 'app-create-variant',
  templateUrl: './create-variant.component.html',
  styleUrls: ['./create-variant.component.scss']
})

export class CreateVariantComponent implements OnInit, OnDestroy {
  isDisabled = false;
  type: string;
  variant: any;
  parts: any;
  variants: any;
  bikeDetails: any;
  partVariantForm: FormGroup;
  types = {
    Create: "Create",
    Update: "Update"
  };
  partVariant = {
    quantity: null,
    active: false,
    partId: null,
    variantName: null,
    workshopId: null,
    variantId: null,
    cost: null
  }
  variantImageFileName: string = null;
  variantImagePreviewUrl: SafeUrl | string = null;
  private variantImageObjectUrl: string = null;
  allWorkshops: any[];
  allPartVariants: any = [];
  filteredWorkshops: any = [];
  filteredVariants: any[] = [];
  allVariants: any[] = [];
  constructor(
    private fb: FormBuilder,
    private repairService: RepairService,
    private loggerService: LoggerService,
    private inventoryService: InventoryService,
    public dialogRef: MatDialogRef<CreateVariantComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private workshopService: WorkshopService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    if (this.data.type == this.types.Update && this.data.variant) {
      this.partVariant.partId = this.data.variant.PartId;
      // this.partVariant.variantName = this.data.variant.VariantName;
      this.partVariant.variantId = this.data.variant.VariantId;
      this.partVariant.quantity = this.data.variant.Quantity;
      this.partVariant.active = this.data.variant.Active;
      this.partVariant.workshopId = this.data.variant.WorkshopId;
      this.partVariant.cost = this.data.variant.Cost;
      if (this.data.variant.VariantImageUrl) {
        this.variantImagePreviewUrl = this.data.variant.VariantImageUrl;
      }
    }
    this.createPartVariantForm();
    this.getAllVariants();
    this.getAllParts();
    this.getAllPartVariants();
    this.getAllWorkshopsWithStreetTeam();
    
    // Initialize form with existing values if updating
    if (this.data.type == this.types.Update && this.data.variant) {
      this.partVariantForm.patchValue({
        PartId: this.data.variant.PartId,
        VariantId: this.data.variant.VariantId,
        Quantity: this.data.variant.Quantity,
        Active: this.data.variant.Active,
        WorkshopId: this.data.variant.WorkshopId,
        Cost: this.data.variant.Cost
      });
    }
  }

  ngOnDestroy(): void {
    if (this.variantImageObjectUrl) {
      URL.revokeObjectURL(this.variantImageObjectUrl);
      this.variantImageObjectUrl = null;
    }
  }

  private createPartVariantForm() {
    this.partVariantForm = this.fb.group({
      PartId: [{ value: "", disabled: this.data.type == this.types.Update }, Validators.required],
      // VariantName: ["", Validators.required],
      VariantId: ["", Validators.required],
      Quantity: ["", Validators.required],
      Active: [false, Validators.required],
      WorkshopId: [{ value: "", disabled: this.data.type == this.types.Update }, Validators.required],
      Cost: [null]
    })
  }

  getAllParts() {
    this.inventoryService.getAllBikeParts().subscribe(data => {
      this.parts = data;
    }, error => {
      this.loggerService.showErrorMessage("Getting parts failed.");
    });
  }

  getAllVariants() {
    this.inventoryService.getAllBikeVariants().subscribe(data => {
      this.allVariants = data || [];
      this.filteredVariants = this.allVariants;
      this.variants = this.filteredVariants;
    }, error => {
      this.loggerService.showErrorMessage("Getting variants failed.");
    });
  }

  onPartChange(event) {
    const selectedPartId = event.value;
    if (selectedPartId) {
      // Filter variants based on the selected parent part
      // For creating new variants, we show all available variants
      // The variant dropdown will be filtered to show variants that can be added
      // (variants that are not already associated with this part in the selected workshop)
      // But initially, we show all variants and let the user select
      this.filteredVariants = this.allVariants;
      this.variants = this.filteredVariants;
      
      // Reset variant selection when part changes
      this.partVariantForm.patchValue({ VariantId: "" });
      this.partVariant.variantId = null;
      this.resetVariantImagePreview();
      
      // Reset workshop filter when part changes
      this.filteredWorkshops = this.allWorkshops;
    } else {
      // If no part selected, show all variants
      this.filteredVariants = this.allVariants;
      this.variants = this.filteredVariants;
      this.resetVariantImagePreview();
    }
  }

  getAllWorkshopsWithStreetTeam() {
    this.allWorkshops = [];
    this.workshopService.getAllWorkshopsWithStreetTeam().subscribe(res => {
      if (res) {
        this.allWorkshops = res;
        this.filteredWorkshops = res;
      }
    });
  }

  createUpdatePartVariant(type: string) {

    this.variant = Object.assign({}, this.partVariantForm.getRawValue());
    // Include cost in the variant object from form value
    const formValue = this.partVariantForm.getRawValue();
    if (formValue.Cost !== null && formValue.Cost !== undefined && formValue.Cost !== '') {
      this.variant['Cost'] = parseFloat(formValue.Cost);
    }
    this.variant['VariantImageUrl'] = this.variantImageFileName ? this.variantImageFileName : null;
    if (!this.validateVariant()) {
      this.loggerService.showWarningMessage("Invalid inputs.");
    }
    else {
      this.isDisabled = true;
      if (type == this.types.Create) {
        this.createVariant();
      }
      else {
        this.variant['Id'] = this.data.variant.Id;
        this.variant['VariantId'] = this.data.variant.VariantId;
        this.updateVariant();
      }
    }
  }

  onVariantChange(event) {
    if (!this.variantImageFileName) {
      const selectedVariant = this.allVariants.find(v => v.VariantId == event.value);
      this.variantImagePreviewUrl = selectedVariant ? (selectedVariant.ImageUrl || selectedVariant.VariantImageUrl || null) : null;
    }
    if (this.allPartVariants) {
      this.filteredWorkshops = this.allWorkshops;
      let filteredPartvariants = this.allPartVariants.filter(x => x.PartId == this.partVariant.partId && x.VariantId == event.value);
      filteredPartvariants.forEach(partVariant => {
        this.filteredWorkshops = this.filteredWorkshops.filter(x => x.Id != partVariant.WorkshopId);
      });
    }
  }

  onVariantImageSelected(event: any) {
    const file = event?.target?.files && event.target.files[0];
    if (!file) {
      this.resetVariantImagePreview(this.getCurrentVariantImage());
      return;
    }
    this.variantImageFileName = this.sanitizeFileName(file.name);
    if (this.variantImageObjectUrl) {
      URL.revokeObjectURL(this.variantImageObjectUrl);
    }
    this.variantImageObjectUrl = URL.createObjectURL(file);
    this.variantImagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.variantImageObjectUrl);
  }

  clearVariantImageSelection() {
    this.resetVariantImagePreview(this.getCurrentVariantImage());
  }

  private resetVariantImagePreview(preview: SafeUrl | string = null) {
    if (this.variantImageObjectUrl) {
      URL.revokeObjectURL(this.variantImageObjectUrl);
      this.variantImageObjectUrl = null;
    }
    this.variantImageFileName = null;
    this.variantImagePreviewUrl = preview || null;
  }

  private getCurrentVariantImage(): string {
    const variantId = this.partVariantForm?.get('VariantId')?.value;
    if (variantId) {
      const selectedVariant = this.allVariants.find(v => v.VariantId == variantId);
      if (selectedVariant) {
        return selectedVariant.ImageUrl || selectedVariant.VariantImageUrl || null;
      }
    }
    if (this.data?.variant?.VariantImageUrl) {
      return this.data.variant.VariantImageUrl;
    }
    return null;
  }

  private sanitizeFileName(original: string): string {
    const dot = original.lastIndexOf('.');
    const name = dot > -1 ? original.substring(0, dot) : original;
    const ext = dot > -1 ? original.substring(dot) : '';
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${slug}${ext.toLowerCase()}`;
  }

  validateVariant(): boolean {
    if (this.variant.PartId == null || this.variant.Quantity == null || this.variant.VariantId == null ||
      this.variant.PartId == "" || this.variant.Quantity == "" || this.variant.VariantId == "") {
      return false;
    }
    return true;
  }

  createVariant() {
    this.inventoryService.createPartVariant(this.variant).subscribe(res => {
      this.dialogRef.close();
      this.loggerService.showSuccessfulMessage("Create part variant successfully.");
    }, error => {
      this.loggerService.showErrorMessage("Error in creating part variant.")
    });
  }

  updateVariant() {
    this.spinner.show();
    this.inventoryService.updatePartVariant(this.variant).subscribe(res => {
      this.dialogRef.close();
      if (!res) {
        this.loggerService.showErrorMessage("Update part variant successfully.But cannot change Default status.");
      }
      else {
        this.loggerService.showSuccessfulMessage("Update part variant successfully.");
      }
    }, error => {
      this.loggerService.showErrorMessage("Error in updating part variant.")
    });
    this.spinner.hide();
  }

  getAllPartVariants() {
    this.inventoryService.getAllPartsAndVariants().subscribe(res => {
      this.allPartVariants = res;
    }, err => {

    });
  }
}